import { combine, createEffect, createEvent, createStore, merge, sample } from 'effector';
import { $openSync } from '../../entities/library';
import { $project, projectClosed } from '../../entities/project';
import { ApiRequestError, NetworkError } from '../../shared/api/http';
import { shares, type ShareLink } from '../../shared/api/shares';
import type { Locale } from '../../shared/lib/locale';

/*
 * The share dialog: one read-only link per project (docs/PLAN.md §3.7). Only projects in the
 * account can be shared, since the link reads the server copy.
 */

export const shareDialogOpened = createEvent();
export const shareDialogClosed = createEvent();
export const createRequested = createEvent();
export const resetRequested = createEvent();
export const stopRequested = createEvent();

/** The link's language: the viewer's own, or a fixed one (`?lang=`). */
export type ShareLanguage = 'viewer' | Locale;
export const languageChanged = createEvent<ShareLanguage>();
export const includeViewChanged = createEvent<boolean>();

export const $shareDialogOpen = createStore(false)
  .on(shareDialogOpened, () => true)
  .reset(shareDialogClosed, projectClosed);

export const $language = createStore<ShareLanguage>('viewer')
  .on(languageChanged, (_, language) => language)
  .reset(projectClosed);
export const $includeView = createStore(true)
  .on(includeViewChanged, (_, include) => include)
  .reset(projectClosed);

export type ShareError = 'offline' | 'notUploaded' | 'failed';
const toError = (error: unknown): ShareError =>
  error instanceof NetworkError
    ? 'offline'
    : error instanceof ApiRequestError && error.status === 404
      ? 'notUploaded'
      : 'failed';

export const loadShareFx = createEffect((projectId: string) => shares.get(projectId));
export const createShareFx = createEffect(
  ({ projectId, rotate }: { projectId: string; rotate: boolean }) =>
    shares.create(projectId, rotate),
);
export const stopShareFx = createEffect((projectId: string) => shares.remove(projectId));

/** The project's link: 'unknown' until loaded, null when it has none. */
export const $link = createStore<ShareLink | null | 'unknown'>('unknown')
  .on(loadShareFx.doneData, (_, link) => link)
  .on(createShareFx.doneData, (_, link) => link)
  .on(stopShareFx.done, () => null)
  .reset(projectClosed);

export const $shareError = createStore<ShareError | null>(null)
  .on(merge([loadShareFx.failData, createShareFx.failData, stopShareFx.failData]), (_, error) =>
    toError(error),
  )
  .reset(loadShareFx, createShareFx, stopShareFx, shareDialogClosed, projectClosed);

export const $busy = combine(
  loadShareFx.pending,
  createShareFx.pending,
  stopShareFx.pending,
  (a, b, c) => a || b || c,
);

/** The project is in the account (sharing possible); signed-out and local-only are not. */
export const $inAccount = $openSync.map((s) => s !== 'none' && s !== 'localOnly');

const $projectId = $project.map((p) => p?.id ?? null);

// Load the link when the dialog opens (or the project was just added to the account).
sample({
  clock: [shareDialogOpened, $inAccount.updates],
  source: { open: $shareDialogOpen, inAccount: $inAccount, id: $projectId },
  filter: ({ open, inAccount, id }) => open && inAccount && id !== null,
  fn: ({ id }) => id as string,
  target: loadShareFx,
});

const withProject = <T>(clock: typeof createRequested, fn: (id: string) => T) =>
  sample({ clock, source: $projectId }).filterMap((id) => (id === null ? undefined : fn(id)));

sample({
  clock: withProject(createRequested, (projectId) => ({ projectId, rotate: false })),
  target: createShareFx,
});
sample({
  clock: withProject(resetRequested, (projectId) => ({ projectId, rotate: true })),
  target: createShareFx,
});
sample({ clock: withProject(stopRequested, (projectId) => projectId), target: stopShareFx });

/**
 * The link to hand out: the token, plus the chosen language and (optionally) the view state of
 * the project page's URL (`view`, `mode`, `v`, …).
 */
export function linkQuery(
  current: Record<string, string>,
  language: ShareLanguage,
  includeView: boolean,
): Record<string, string> {
  const query: Record<string, string> = {};
  if (language !== 'viewer') query.lang = language;
  if (includeView) {
    for (const [key, value] of Object.entries(current)) if (key !== 'lang') query[key] = value;
  }
  return query;
}
